import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Zap, Shield } from "lucide-react";

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 1, 0.5, 1] } },
};

export default function Hero() {
  return (
    <section className="relative pt-20 pb-24 sm:pt-28 sm:pb-36 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 -z-10 grain pointer-events-none" />
      <div className="absolute right-[-10%] top-10 -z-10 h-[420px] w-[420px] rounded-full bg-terra/10 blur-3xl" />
      <div className="absolute left-[-8%] bottom-0 -z-10 h-[300px] w-[300px] rounded-full bg-ink/5 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div variants={stagger} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <motion.div variants={item} className="eyebrow mb-5">SEO · Digital PR · Authority</motion.div>
            <motion.h1
              variants={item}
              className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight font-bold leading-[1.02]"
              data-testid="hero-title"
            >
              Scalable SEO &<br />
              Digital PR for{" "}
              <span className="text-terra">agencies</span>{" "}
              <br className="hidden sm:block" />
              and brands.
            </motion.h1>
            <motion.p variants={item} className="mt-6 text-lg text-inkSoft max-w-xl leading-relaxed">
              Order guest posts, press releases, link building, content syndication, local SEO and technical optimization — all in one quiet workspace. White-label ready, agency-grade reporting.
            </motion.p>
            <motion.div variants={item} className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/services" className="btn-primary" data-testid="hero-view-services">
                View Services <ArrowUpRight size={18} />
              </Link>
              <Link to="/signup" className="btn-ghost" data-testid="hero-get-started">
                Get Started
              </Link>
            </motion.div>
            <motion.div variants={item} className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-inkSoft">
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={14} className="text-terra" /> White-label fulfillment</span>
              <span className="inline-flex items-center gap-2"><Zap size={14} className="text-terra" /> 5-21 day turnaround</span>
              <span className="inline-flex items-center gap-2"><Shield size={14} className="text-terra" /> SEO-safe placements</span>
            </motion.div>
          </div>

          <motion.div variants={item} className="lg:col-span-5 relative">
            <div className="card-soft p-6 relative overflow-hidden">
              <div className="flex items-center justify-between">
                <div className="eyebrow">Live · Campaigns</div>
                <div className="font-mono text-xs text-inkSoft">+12.4% MoM</div>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3">
                {[{ k: "Orders", v: "1,402" }, { k: "Placements", v: "986" }, { k: "DR avg", v: "47.6" }].map((s) => (
                  <div key={s.k} className="rounded-xl border border-line p-3">
                    <div className="font-mono text-2xl font-semibold tracking-tighter">{s.v}</div>
                    <div className="text-[11px] uppercase tracking-widest text-inkSoft mt-1">{s.k}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 space-y-2.5">
                {[
                  { t: "Press release · Aperture Goods", s: "delivered", d: "today" },
                  { t: "Guest post · DR 60-70 · SaaS", s: "in_progress", d: "2d" },
                  { t: "Site audit · Northbeam Studio", s: "in_review", d: "1d" },
                ].map((r) => (
                  <div key={r.t} className="flex items-center justify-between text-sm py-2.5 border-t border-line">
                    <div className="truncate pr-2">{r.t}</div>
                    <span className={`status-pill status-${r.s}`}>{r.s.replace("_", " ")}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden md:block absolute -bottom-8 -left-8 card-soft w-56 p-4 rotate-[-3deg]">
              <div className="eyebrow mb-1">Domain rating</div>
              <div className="font-mono text-4xl font-semibold tracking-tighter">+19</div>
              <div className="text-xs text-inkSoft mt-1">28 → 47 in 6 months</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
